import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Cropper from 'react-cropper';
import { readAsDataURL } from 'promise-file-reader';

import styles from './imageCropperStyles.scss';

import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import ErrorMessage from 'buyside/components/presentation/ErrorMessage/errorMessage';

import TrashIcon from 'buyside/svgs/trash.svg';
import PhotoIcon from 'buyside/svgs/photo.svg';
import CropIcon from 'buyside/svgs/crop.svg';

import { Maybe, UpdateAllocatorProfileBackgroundImageMutation } from 'interfaces/graphql-typings';

const ImageCropper: React.FC<{
  aspectRatio: number;
  defaultImage?: string;
  image: string;
  onClose: () => void;
  saveImage: (args: {
    removeImage?: boolean;
    cropData?: Cropper.Data;
    imageData?: Maybe<string>;
    croppedImageData?: Maybe<string>;
  }) => Promise<UpdateAllocatorProfileBackgroundImageMutation>;
  title: string;
}> = ({ aspectRatio, defaultImage, image, onClose, saveImage, title }) => {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [src, setSrc] = useState(image || defaultImage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [newImage, setNewImage] = useState(false);
  const [cropBox, setCropBox] = useState<Cropper.Data | null>();

  const cropper = useRef<Cropper>(null);

  useEffect(() => {
    if (cropperOpen === true && cropBox && cropper?.current) {
      cropper.current.setData(cropBox);
    }
  }, [cropper, cropBox, cropperOpen]);

  const isDefaultImage = src === defaultImage;

  return (
    <div className={styles.imageCropperWrapper}>
      {loading && (
        <div className={styles.loadingArea}>
          <LoadingSpinner size="medium" />
        </div>
      )}
      <h2 className={styles.cropperHeader}>
        {cropperOpen ? `Drag to crop your ${title}` : `Your current ${title}`}
      </h2>
      {error && (
        <ErrorMessage title="Uh Oh...">
          There was a problem with the cropper. Please refresh the page. If the problem persists,
          contact support@sumzero.com.
        </ErrorMessage>
      )}
      <Cropper
        ref={cropper}
        src={src}
        aspectRatio={aspectRatio}
        minCanvasHeight={300}
        minContainerHeight={300}
        zoomable={false}
        className={classNames(styles.cropper, { [styles.hidden]: !cropperOpen })}
      />
      <div className={classNames(styles.imageDisplayArea, { [styles.hidden]: cropperOpen })}>
        <img src={src} className={styles.cropperImage} />
      </div>
      {(cropperOpen && (
        <nav className={`${styles.cropperFooter} ${styles.cropperOpen}`}>
          <div className={styles.cancelButton} onClick={(): void => setCropperOpen(false)}>
            CANCEL
          </div>
          <Button
            text="Save crop"
            size={BUTTON_SIZES.TINY}
            type={BUTTON_TYPES.PRIMARY}
            onClick={(): void => {
              const cropData = cropper?.current?.getData();
              setCropBox(cropData);
              setLoading(true);

              const croppedImageData = cropper?.current?.getCroppedCanvas()?.toDataURL();

              saveImage({
                cropData,
                imageData: newImage ? src : null,
                croppedImageData
              }).then(
                () => {
                  setCropperOpen(false);
                  setLoading(false);
                  setError(false);
                  onClose();
                },
                () => {
                  setLoading(false);
                  setError(true);
                }
              );
              setNewImage(false);
            }}
            isLoading={loading}
          />
        </nav>
      )) || (
        <nav className={styles.cropperFooter}>
          {!isDefaultImage && (
            <div
              className={styles.removeButton}
              onClick={(): void => {
                setSrc(defaultImage);
                setCropBox(null);
                setLoading(true);

                saveImage({ removeImage: true }).then(
                  () => {
                    setLoading(false);
                    setError(false);
                    onClose();
                  },
                  () => {
                    setLoading(false);
                    setError(true);
                  }
                );
              }}
            >
              <TrashIcon className={styles.trashIcon} />
              REMOVE {title.toUpperCase()}
            </div>
          )}
          <div
            className={classNames(styles.uploadImage, { [styles.defaultImage]: isDefaultImage })}
          >
            <label className={styles.uploadButton}>
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={(e: DragEvent & ChangeEvent<HTMLInputElement>): Promise<void> | null => {
                  e.preventDefault();
                  const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;

                  if (!files) return null;

                  const file = files[0];
                  return readAsDataURL(file).then(result => {
                    setSrc(result);
                    setCropperOpen(true);
                    setNewImage(true);
                  });
                }}
              />
              <PhotoIcon className={styles.photoIcon} />
              UPLOAD NEW {title.toUpperCase()}
            </label>
          </div>
          {!isDefaultImage && (
            <div className={styles.cropButton} onClick={(): void => setCropperOpen(true)}>
              <CropIcon className={styles.cropIcon} />
              CROP {title.toUpperCase()}
            </div>
          )}
        </nav>
      )}
    </div>
  );
};

export default ImageCropper;
